import { z } from "zod/v4"
import { UserId, UserIdSchema, StoryId, StoryIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"

// StoryDocument

export const StoryDocumentMeta = z.object({
  message: z.string().describe("ストーリーのメッセージ（1文字以上500文字以下）"),
})

export type StoryDocumentMeta = z.infer<typeof StoryDocumentMeta>

export const StoryDocumentBody = z.intersection(
  StoryDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type StoryDocumentBody = z.infer<typeof StoryDocumentBody>

export const StoryDocument = z.intersection(
  StoryDocumentBody,
  z.object({
    id: StoryIdSchema,
  })
)

export type StoryDocument = z.infer<typeof StoryDocument>

export class StoryDocuments {
  /** ストーリーの有効期限（時間） */
  static readonly STORY_EXPIRY_HOURS = 24

  static toDocumentData(story: StoryDocument): object {
    return {
      ...story,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): StoryDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: StoryId(doc.id),
      message: data["message"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }

}


export const StoryDenormalized = z.object({
  id: StoryIdSchema,
  message: z.string().describe("ストーリーのメッセージ"),
  created_at: CreatedAt,
  updated_at: UpdatedAt,
})

export type StoryDenormalized = z.infer<typeof StoryDenormalized>

export const StoryTimelineDocumentMeta = z.object({
  stories: z.array(StoryDenormalized).describe("Storyの非正規化情報"),
  has_unread: z.boolean().describe("stories 内に未読があるかどうか"),
  read_at: z.number().nullable().describe("前回開いた時刻。この時刻以前のストーリーは全て既読扱い"),
  last_created_at: z.number().describe("stories.created_at の中で最も新しい時刻"),
})

export type StoryTimelineDocumentMeta = z.infer<typeof StoryTimelineDocumentMeta>

export const StoryTimelineDocumentBody = z.intersection(
  StoryTimelineDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type StoryTimelineDocumentBody = z.infer<typeof StoryTimelineDocumentBody>

export const StoryTimelineDocument = z.intersection(
  StoryTimelineDocumentBody,
  z.object({
    id: UserIdSchema, // followeeId
  })
)

export type StoryTimelineDocument = z.infer<typeof StoryTimelineDocument>

export class StoryTimelineDocuments {

  static toDocumentData(timeline: StoryTimelineDocument): object {
    return {
      ...timeline,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): StoryTimelineDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: UserId(doc.id),
      stories: data["stories"],
      has_unread: data["has_unread"],
      read_at: data["read_at"],
      last_created_at: data["last_created_at"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }

}

export const CreateStoryRequest = z.object({
  message: z.string().min(1).max(500),
})
export type CreateStoryRequest = z.infer<typeof CreateStoryRequest>

export const CreateStoryResponse = z.object({
  id: z.string(),
})
export type CreateStoryResponse = z.infer<typeof CreateStoryResponse>
